import React, { useEffect, useState } from "react";
import "./DigitalOMR.css";
import { useHistory } from "react-router-dom";
import OmrSheetModal from "./OmrSheetModal";
import OmrApi, { omrError } from "./omrApi";
import Notification from "components/Notification";
import Loader from "components/Loader";
import Visibility from "@material-ui/icons/Visibility";
import Assessment from "@material-ui/icons/Assessment";
import GetApp from "@material-ui/icons/GetApp";
import VpnKey from "@material-ui/icons/VpnKey";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Edit from "@material-ui/icons/Edit";
import Publish from "@material-ui/icons/Publish";

const ico = { fontSize: 16, verticalAlign: "middle" };
const EMPTY = {};

// Subject may arrive as a plain string (legacy) or as a related object
// ({ id, name }) now that subjects are a managed entity.
const subjectName = (paper) => {
  if (!paper) return "";
  const s = paper.subject;
  if (s && typeof s === "object") return s.name || "";
  return s || paper.subject_name || "";
};

export default function Dashboard() {
  const history = useHistory();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [meta, setMeta] = useState({ total: 0, from: 0, to: 0, last_page: 1 });

  const [notif, setNotif] = useState(null);
  const [view, setView] = useState(null); // { paper, answers }
  const [viewLoading, setViewLoading] = useState(false);
  const [delRow, setDelRow] = useState(null);
  const [pubRow, setPubRow] = useState(null); // paper pending publish confirm
  const [busyId, setBusyId] = useState(null); // row id mid publish/delete

  const notify = (type, message) => setNotif({ type, message, id: Date.now() });

  // Combine search + status + page so filters never reset each other.
  const fetchPapers = (opts = {}) => {
    const params = {
      search,
      status,
      per_page: perPage,
      page,
      ...opts,
    };
    setLoading(true);
    OmrApi.listTestPapers(params)
      .then((resp) => {
        const d = (resp.data && resp.data.data) || {};
        setRows(d.data || []);
        setMeta({
          total: d.total || 0,
          from: d.from || 0,
          to: d.to || 0,
          last_page: d.last_page || 1,
        });
        setPage(d.current_page || 1);
      })
      .catch((err) => notify("error", omrError(err, "Failed to load test papers.")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPapers({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    setPage(1);
    fetchPapers({ page: 1 });
  };
  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setPage(1);
    fetchPapers({ search: "", status: "", page: 1 });
  };
  const changeLimit = (n) => {
    setPerPage(n);
    setPage(1);
    fetchPapers({ per_page: n, page: 1 });
  };
  const goPage = (p) => {
    if (p < 1 || p > meta.last_page) return;
    setPage(p);
    fetchPapers({ page: p });
  };

  // View OMR — fetch the saved answer key and show it in the read-only preview.
  const openView = (paper) => {
    setViewLoading(true);
    OmrApi.getTestPaper(paper.id)
      .then((resp) => {
        const d = (resp.data && resp.data.data) || {};
        const answers = {};
        (d.answer_keys || []).forEach((a) => {
          answers[a.question_number] = a.correct_answer;
        });
        setView({ paper: d, answers });
      })
      .catch((err) => notify("error", omrError(err, "Failed to load the answer sheet.")))
      .finally(() => setViewLoading(false));
  };

  const download = (url, label) => {
    if (!url) {
      notify("error", `No ${label} uploaded for this paper.`);
      return;
    }
    window.open(url, "_blank");
  };

  const doPublish = () => {
    if (!pubRow) return;
    const id = pubRow.id;
    setBusyId(id);
    setPubRow(null);
    OmrApi.publishTestPaper(id)
      .then((resp) =>
        notify("success", (resp.data && resp.data.message) || "Test paper published."),
      )
      .then(() => fetchPapers())
      .catch((err) =>
        notify(
          "error",
          omrError(err, "Publish failed — make sure a complete answer key is saved."),
        ),
      )
      .finally(() => setBusyId(null));
  };

  const doDelete = () => {
    if (!delRow) return;
    const id = delRow.id;
    setBusyId(id);
    OmrApi.deleteTestPaper(id)
      .then((resp) => {
        notify("success", (resp.data && resp.data.message) || "Test paper deleted.");
        setDelRow(null);
        fetchPapers();
      })
      .catch((err) => notify("error", omrError(err, "Delete failed.")))
      .finally(() => setBusyId(null));
  };

  return (
    <div className="domr-page">
      {notif && (
        <Notification
          key={notif.id}
          type={notif.type}
          title={notif.message}
          duration={3500}
        />
      )}

      <h1 className="domr-page-title">Dashboard</h1>
      <p className="domr-page-sub">Manage your Digital OMR test papers.</p>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-end",
          marginBottom: 16,
        }}
      >
        <div className="domr-field">
          <label>Search</label>
          <input
            className="domr-input"
            style={{ height: 40, minWidth: 240 }}
            placeholder="Name / code / subject"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
          />
        </div>
        <div className="domr-field">
          <label>Status</label>
          <select
            className="domr-input"
            style={{ height: 40, minWidth: 140 }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
        <button className="domr-btn domr-btn-primary" onClick={applyFilters}>
          Search
        </button>
        <button className="domr-btn domr-btn-ghost" onClick={clearFilters}>
          Clear
        </button>
        <button
          className="domr-btn domr-btn-ghost"
          style={{ marginLeft: "auto" }}
          onClick={() => history.push("/admin/omr-master")}
        >
          + New Test Paper
        </button>
      </div>

      <div className="domr-table-card">
        <table className="domr-table">
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Name</th>
              <th>Paper Code</th>
              <th>Subject</th>
              <th>Questions</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="domr-empty">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="domr-empty">
                  No test papers found.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r.id}>
                  <td>{meta.from + i}</td>
                  <td>{r.name}</td>
                  <td>
                    <span className="domr-code-pill">{r.code}</span>
                  </td>
                  <td>{subjectName(r)}</td>
                  <td>{r.no_of_questions}</td>
                  <td>{r.duration_minutes} Mins</td>
                  <td>
                    {r.status === "draft" ? (
                      <button
                        className="domr-status draft"
                        disabled={busyId === r.id}
                        title="Click to publish this paper"
                        onClick={() => setPubRow(r)}
                      >
                        {busyId === r.id ? "Publishing…" : "draft"}
                        {busyId !== r.id && (
                          <span className="domr-status-cta">› Publish</span>
                        )}
                      </button>
                    ) : (
                      <span className={"domr-status " + r.status}>
                        {r.status}
                      </span>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="domr-action blue" onClick={() => openView(r)}>
                      <Visibility style={ico} /> View OMR
                    </button>
                    <button
                      className="domr-action indigo"
                      onClick={() => history.push(`/admin/omr-report/${r.id}`)}
                    >
                      <Assessment style={ico} /> Report
                    </button>
                    <button
                      className="domr-action violet"
                      onClick={() => download(r.paper_pdf_url, "question paper")}
                    >
                      <GetApp style={ico} /> Paper
                    </button>
                    <button
                      className="domr-action green"
                      onClick={() => download(r.answer_key_pdf_url, "answer key")}
                    >
                      <VpnKey style={ico} /> Key
                    </button>
                    <button
                      className="domr-action blue"
                      onClick={() => history.push(`/admin/omr-edit/${r.id}`)}
                    >
                      <Edit style={ico} /> Edit
                    </button>
                    <button
                      className="domr-action red"
                      disabled={busyId === r.id}
                      onClick={() => setDelRow(r)}
                    >
                      <DeleteOutline style={{ fontSize: 18 }} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination — entries message on the left, limit + Prev/Next on the right */}
      {!loading && rows.length > 0 && (
        <div className="domr-pager">
          <span>
            Showing {meta.from} to {meta.to} of {meta.total} entries
          </span>
          <div className="domr-pager-btns">
            <label className="domr-limit">
              Show
              <select
                value={perPage}
                onChange={(e) => changeLimit(Number(e.target.value))}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <button onClick={() => goPage(page - 1)} disabled={page <= 1}>
              Prev
            </button>
            <span>
              Page {page} of {meta.last_page}
            </span>
            <button onClick={() => goPage(page + 1)} disabled={page >= meta.last_page}>
              Next
            </button>
          </div>
        </div>
      )}

      {/* Full-screen spinner while loading a paper's answer key */}
      {viewLoading && (
        <div className="domr-overlay" style={{ alignItems: "center" }}>
          <div style={{ color: "#fff" }}>
            <Loader />
          </div>
        </div>
      )}

      {/* View OMR — read-only answer-key preview */}
      <OmrSheetModal
        open={!!view}
        readOnly
        title={view ? `${subjectName(view.paper)} - OMR Sheet` : ""}
        count={view ? view.paper.no_of_questions : 0}
        initialAnswers={view ? view.answers : EMPTY}
        onClose={() => setView(null)}
      />

      {/* Publish confirm */}
      {pubRow && (
        <div className="domr-overlay" style={{ alignItems: "center" }} onClick={() => setPubRow(null)}>
          <div className="domr-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="domr-confirm-head">
              <span className="domr-confirm-ico" style={{ background: "#dcfce7", color: "#15803d" }}>
                <Publish />
              </span>
              <h3>Publish “{pubRow.name}”?</h3>
            </div>
            <p>
              Published papers become available to students. Make sure a complete
              answer key is saved first.
            </p>
            <div className="domr-form-actions">
              <button className="domr-btn domr-btn-ghost" onClick={() => setPubRow(null)}>
                Cancel
              </button>
              <button
                className="domr-btn domr-btn-primary"
                style={{ background: "linear-gradient(90deg,#22c55e,#16a34a)", boxShadow: "none" }}
                onClick={doPublish}
              >
                Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {delRow && (
        <div className="domr-overlay" style={{ alignItems: "center" }} onClick={() => setDelRow(null)}>
          <div
            className="domr-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="domr-confirm-head">
              <span className="domr-confirm-ico">
                <DeleteOutline />
              </span>
              <h3>Delete “{delRow.name}”?</h3>
            </div>
            <p>This will remove the test paper. This action can’t be undone here.</p>
            <div className="domr-form-actions">
              <button className="domr-btn domr-btn-ghost" onClick={() => setDelRow(null)}>
                Cancel
              </button>
              <button
                className="domr-btn domr-btn-primary"
                style={{ background: "linear-gradient(90deg,#ef4444,#dc2626)", boxShadow: "none" }}
                disabled={busyId === delRow.id}
                onClick={doDelete}
              >
                {busyId === delRow.id ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
